"use client";

import { useRef, useState, type JSX } from "react";
import { cn } from "@repo/ui/utils";
import { Icon } from "@repo/ui/icon";

interface AudioPlayerProps {
  /** Resolved audio file URL (from the Sanity file asset). */
  src: string;
  /** Optional label shown next to the play/pause control. */
  title?: string;
  className?: string;
}

/**
 * Audio clip player for rich text — a play/pause toggle (design-system icon)
 * with a text label. No progress bar; it just drives a hidden <audio>.
 */
export function AudioPlayer({
  src,
  title,
  className,
}: AudioPlayerProps): JSX.Element | null {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  if (!src) return null;

  const toggle = (): void => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().catch(() => {
        // Ignore play() rejections (e.g. interrupted by a quick re-click).
      });
    } else {
      el.pause();
    }
  };

  return (
    <div className={cn("flex items-center gap-4", className)}>
      {title && <span className="typography-body-md-medium">{title}</span>}
      <button
        type="button"
        onClick={toggle}
        aria-label={isPlaying ? "Pause audio" : "Play audio"}
        className="flex size-12 shrink-0 cursor-pointer items-center justify-center rounded-full bg-sunshine-yellow-500 transition-colors hover:bg-sunshine-yellow-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2"
      >
        <Icon
          name={isPlaying ? "pause" : "play-arrow"}
          size="lg"
          filled
          className="text-deep-sea-blue-500"
        />
      </button>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        className="hidden"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
      />
    </div>
  );
}
